import { formatDate } from 'date-fns/format';
import useGetInvoiceQuery from '@/api/queries/useGetInvoiceQuery';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { formatInvoiceLineProduct } from '@/models/Invoice';
import { AdminBillingInvoiceListPath } from '@/modules/admin/paths';
import { BillingInvoiceListPath } from '@/modules/billing/paths';
import { formatPrice } from '@/utils/currency';
import { formatCityRegionPostal } from '@/utils/formatAddress';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useLocation, useParams } from 'react-router-dom';

const InvoiceDetails = () => {
  const location = useLocation();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { invoice, isLoading, error } = useGetInvoiceQuery(invoiceId);
  const backPath = location.pathname.startsWith('/admin/')
    ? AdminBillingInvoiceListPath
    : BillingInvoiceListPath;
  const handleDownloadPdf = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <>
        <PageHeader title="Invoice details">
          <Button to={backPath} variant="secondary">
            Back to invoices
          </Button>
          <Button
            onClick={handleDownloadPdf}
            variant="secondary"
            icon={<ArrowDownTrayIcon className="h-4 w-4" />}
          >
            Download PDF
          </Button>
        </PageHeader>

        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load invoice details.
        </div>
      </>
    );
  }

  const billingAddress = invoice.billingDetails.address;
  const billingAddressLine = billingAddress ? formatCityRegionPostal(billingAddress) : '';

  return (
    <>
      <PageHeader title="Invoice details">
        <Button to={backPath} variant="secondary">
          Back to invoices
        </Button>
        <Button
          onClick={handleDownloadPdf}
          variant="secondary"
          icon={<ArrowDownTrayIcon className="h-4 w-4" />}
        >
          Download PDF
        </Button>
      </PageHeader>

      <div className="invoice-details-layout grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="invoice-details-card rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <div className="flex flex-col gap-2 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-sm font-medium text-slate-500">Invoice number</div>
              <div
                className="mt-1 text-2xl font-semibold text-slate-900"
                data-testid="invoice-details-number"
              >
                {invoice.invoiceNumber}
              </div>
              <div
                className="mt-2 text-sm text-slate-600"
                data-testid="invoice-details-description"
              >
                {invoice.description}
              </div>
            </div>

            <div
              className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium capitalize text-slate-700"
              data-testid="invoice-details-status"
            >
              {invoice.status}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <div>
              <div className="text-sm font-medium text-slate-500">Created</div>
              <div className="mt-1 text-sm text-slate-900">
                {formatDate(invoice.createdAt, 'd MMM yyyy')}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-500">Updated</div>
              <div className="mt-1 text-sm text-slate-900">
                {formatDate(invoice.updatedAt, 'd MMM yyyy')}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-500">Billing period</div>
              <div className="mt-1 text-sm text-slate-900">
                {formatDate(invoice.period.start, 'd MMM yyyy')} -{' '}
                {formatDate(invoice.period.end, 'd MMM yyyy')}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium text-slate-500">Payment status</div>
              <div
                className="mt-1 text-sm text-slate-900 capitalize"
                data-testid="invoice-details-payment-status"
              >
                {invoice.paymentStatus ?? 'not started'}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <div className="mb-4 text-sm font-medium text-slate-500">Invoice lines</div>

            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      Product
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      Quantity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      Unit price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      VAT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {invoice.lines.map((line, index) => (
                    <tr key={`${line.product}-${index}`} data-testid="invoice-details-line-row">
                      <td className="px-4 py-4 align-top">
                        <div
                          className="font-medium text-slate-900"
                          data-testid="invoice-details-line-product"
                        >
                          {formatInvoiceLineProduct(line.product)}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">{line.quantity}</td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {formatPrice(line.unitPrice)}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {line.vatRate !== undefined ? `${line.vatRate}%` : '-'}
                        <div className="mt-1 text-xs text-slate-500">
                          {formatPrice(line.vatAmount)}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-slate-900">
                        {formatPrice(line.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <aside className="space-y-6">
          <section className="invoice-details-card rounded-xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-medium text-slate-500">Totals</div>
            <dl className="mt-4 space-y-3">
              <div className="flex items-center justify-between gap-4 text-sm">
                <dt className="text-slate-600">Subtotal</dt>
                <dd className="font-medium text-slate-900">
                  {formatPrice(invoice.subtotalAmount)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4 text-sm">
                <dt className="text-slate-600">Tax</dt>
                <dd className="font-medium text-slate-900">{formatPrice(invoice.taxAmount)}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 border-t border-slate-200 pt-3 text-sm">
                <dt className="font-medium text-slate-900">Total</dt>
                <dd
                  className="text-base font-semibold text-slate-900"
                  data-testid="invoice-details-total"
                >
                  {formatPrice(invoice.totalAmount)}
                </dd>
              </div>
            </dl>
          </section>

          <section className="invoice-details-card rounded-xl border border-slate-200 bg-white p-6">
            <div className="text-sm font-medium text-slate-500">Billing details</div>
            <div className="mt-4 space-y-1 text-sm text-slate-700">
              <div
                className="break-words font-medium text-slate-900"
                data-testid="invoice-details-billing-company"
              >
                {invoice.billingDetails.companyName || 'Not provided'}
              </div>
              <div className="break-words" data-testid="invoice-details-billing-email">
                {invoice.billingDetails.billingEmail || 'No billing email'}
              </div>
              {invoice.billingDetails.taxId ? (
                <div>Tax ID: {invoice.billingDetails.taxId}</div>
              ) : null}
            </div>

            {billingAddress ? (
              <div className="mt-4 border-t border-slate-200 pt-4 text-sm text-slate-700">
                <div>{billingAddress.building}</div>
                <div>{billingAddress.street1}</div>
                {billingAddress.street2 ? <div>{billingAddress.street2}</div> : null}
                {billingAddressLine ? <div>{billingAddressLine}</div> : null}
                <div>{billingAddress.countryCode}</div>
              </div>
            ) : null}
          </section>
        </aside>
      </div>
    </>
  );
};

export default InvoiceDetails;
